import classNames from 'classnames';
import React from 'react';
import { BookingWizardData } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/BaseBookingWizardData';
import { BookingAddOn } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/BookingAddOn';
import { useBookingWizardDataStore } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardDataStore';
import { getWizardDataAddOns } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/AddOn/AddOnOption';
import { CustomTooltip } from 'Views/Components/_HumanWritten/CustomTooltip/CustomTooltip';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { AddOnInputCounter } from './AddOnInputCounter';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import { newConfirmModal } from 'Views/Components/Modal/NewConfirmModal';
import { tripDirectionTypes } from 'Models/Enums';
import { AdditionalBookingOptionEntity } from 'Models/Entities';
import { AddOnCapacityRestrictionDto } from 'Services/Api/_HumanWritten/BookingWizardDataService';
import If from '../If/If';
import moment from 'moment/moment';
import addOnStore from 'Models/AddOnStore';
import { getFerryAndRouteIds } from 'Util/_HumanWritten/BookingWizard/BookingWizardWrapUtils';
import useAddOnsContext from 'Hooks/useAddOnsContext';
import { getStopByIdIfAny } from 'Global/JourneyElements/Utils/getStopById';
import { store } from 'Models/Store';
import { canAdjustRestrictedAddOns } from 'Util/_HumanWritten/addOnPermissions';

interface AddOnInputCounterProps {
	addOn: AdditionalBookingOptionEntity;
	direction: tripDirectionTypes;
	capacityRestriction?: AddOnCapacityRestrictionDto;
	maxSectionAddOnPerBooking?: number;
	sectionAddOns: AdditionalBookingOptionEntity[];
	sectionDisabled?: boolean;
}

export function AddOnInputCounterWrapper({
	addOn,
	direction,
	capacityRestriction,
	maxSectionAddOnPerBooking,
	sectionAddOns,
	sectionDisabled,
}: AddOnInputCounterProps) {
	const { wizardData, setWizardData, oldWizardData } = useBookingWizardDataStore();
	const { selectedTrips } = useAddOnsContext();

	const isRestrictedLocked = addOn.isRestricted && !canAdjustRestrictedAddOns();
	const effectivelyDisabled = addOn.disabled || sectionDisabled;

	let disabled = false;
	const availableForRouteAndOrFerryTripBookingWizardData = addOnStore
		.getAddOnSectionsByFerriesAndRoutes(
			getFerryAndRouteIds(selectedTrips, direction === 'DEPARTURE' ? 'departing' : 'returning'),
		)
		.some(x => x.additionalBookingOptions.some(y => y.id === addOn.id));

	const addAddOn = (addOns: BookingAddOn[]) => {
		const option = addOns.find(x => x.optionId === addOn.id);
		if (option) {
			option.amount += 1;
			option.adjustedBySystem = false;
		} else {
			addOns.push({
				optionId: addOn.id,
				optionName: addOn.name,
				optionPrice: addOn.staticPrice,
				gstExempt: addOn.excludeGST,
				amount: 1,
				adjustedBySystem: false,
			});
		}
	};

	const onAdd = () => {
		const newData = new BookingWizardData({ ...wizardData });

		addAddOn(getWizardDataAddOns(newData, direction));
		setWizardData(newData);
	};

	const removeAddOn = (addOns: BookingAddOn[]) => {
		const option = addOns.find(x => x.optionId === addOn.id);
		if (option) {
			option.amount -= 1;
			option.adjustedBySystem = false;
		}
	};

	const onSubtract = () => {
		// If the add-on is disabled and removing would reach 0, show a confirmation
		if (effectivelyDisabled) {
			const currentAmount = amount();
			if (currentAmount === 1) {
				newConfirmModal(
					'Remove add-on?',
					'This add-on is no longer available. You can re-add it before completing this booking, but it will not be available for future bookings.',
					{
						confirmText: 'Remove',
						cancelText: 'Keep',
					},
				)
					.then(() => {
						const newData = new BookingWizardData({ ...wizardData });
						removeAddOn(getWizardDataAddOns(newData, direction));
						setWizardData(newData);
					})
					.catch(() => {
						// User chose to keep the add-on
					});
				return;
			}
		}

		const newData = new BookingWizardData({ ...wizardData });

		removeAddOn(getWizardDataAddOns(newData, direction));
		setWizardData(newData);
	};

	const subtractDisabled = () => {
		return canNotBeRemoved(direction) || disabled;
	};

	const canNotBeAdded = (directionToAddTo: tripDirectionTypes) => {
		// Disabled add-ons can only be re-added up to the original amount from the booking
		if (effectivelyDisabled) {
			if (oldWizardData?.wizardMode === 'ALTERATION') {
				const originalOptions =
					directionToAddTo === 'DEPARTURE'
						? oldWizardData.innerWizardData.departingTripOptions
						: oldWizardData.innerWizardData.returningTripOptions;
				const originalAmount = originalOptions?.find(x => x.optionId === addOn.id)?.amount ?? 0;
				const currentAddOns = getWizardDataAddOns(wizardData, directionToAddTo);
				const currentAmount = currentAddOns.find(x => x.optionId === addOn.id)?.amount ?? 0;
				return currentAmount >= originalAmount;
			}
			return true;
		}

		const addOns = getWizardDataAddOns(wizardData, directionToAddTo);
		const option = addOns.find(x => x.optionId === addOn.id);

		// If not a staff member and there is a capacity restriction and the remaining capacity is 0 or the amount is the same as the remaining capacity, then we can't add it
		if (
			!store.isStaff &&
			isNotNullOrUndefined(capacityRestriction) &&
			(capacityRestriction.remainingCapacity === 0 || option?.amount === capacityRestriction.remainingCapacity)
		) {
			return true;
		}

		// If staff member, add-on can't be overbooked and there is 0 or less capacity remaining, can't add
		if (
			store.isStaff &&
			!capacityRestriction?.canBeOverbooked &&
			isNotNullOrUndefined(capacityRestriction) &&
			(option?.amount ?? 0) >= (capacityRestriction?.remainingCapacity ?? 0)
		) {
			return true;
		}

		if (maxSectionAddOnPerBooking && maxSectionAddOnPerBooking > 0) {
			// I want to disable add when we have reached a total number of section add-ons equal to the max limit.
			const sectionAddOnIds = sectionAddOns.map(x => x.id);
			const selectedAddOns = addOns.filter(x => sectionAddOnIds.includes(x.optionId));
			const totalSectionAddOns = selectedAddOns.map(x => x.amount).reduce((prev, curr) => prev + curr, 0);
			if (totalSectionAddOns >= maxSectionAddOnPerBooking) {
				return true;
			}
		}

		if (option) {
			// If it's a singular add-on, and it already has more than 0 amount, then we can't add it.
			if (addOn.isSingular) {
				return option.amount > 0;
			}
		}

		return false;
	};

	const canNotBeRemoved = (directionToAddTo: tripDirectionTypes) => {
		const addOns = getWizardDataAddOns(wizardData, directionToAddTo);

		let minimum = 0;

		if ((!addOn.removable || wizardData.restrictedMode) && oldWizardData?.wizardMode === 'ALTERATION') {
			const originalOption =
				directionToAddTo === 'DEPARTURE'
					? oldWizardData?.innerWizardData?.departingTripOptions?.find(x => x.optionId === addOn.id)
					: oldWizardData?.innerWizardData?.returningTripOptions?.find(x => x.optionId === addOn.id);
			minimum = originalOption?.amount ?? 0;
		}

		const option = addOns.find(x => x.optionId === addOn.id);
		if (option) {
			return option.amount <= minimum;
		}

		return true;
	};

	const hasBeenChangedByTheSystem = () => {
		const addOns = getWizardDataAddOns(wizardData, direction);
		const option = addOns.find(x => x.optionId === addOn.id);
		return option?.adjustedBySystem ?? false;
	};

	const addDisabled = () => {
		return canNotBeAdded(direction) || disabled;
	};

	const amount = () => {
		const addOns = getWizardDataAddOns(wizardData, direction);
		const option = addOns.find(x => x.optionId === addOn.id);

		if (option) {
			return option.amount;
		}

		return 0;
	};

	const date = () => {
		let unformattedDate = wizardData.departingJourney?.departureDateTime;
		if (direction === 'RETURN') {
			unformattedDate = wizardData.returningJourney?.departureDateTime;
		}
		return moment(unformattedDate).format('DD MMM YYYY');
	};

	let tagText = null;
	let tagColour = null;
	if (
		isNotNullOrUndefined(capacityRestriction?.remainingCapacity) &&
		isNotNullOrUndefined(capacityRestriction?.initialCapacity)
	) {
		const remainingPercentage = Math.round(
			(capacityRestriction!.remainingCapacity / capacityRestriction!.initialCapacity) * 100,
		);
		if (
			capacityRestriction!.remainingCapacity === 0 ||
			(!store.isStaff && capacityRestriction!.remainingCapacity < 0)
		) {
			tagText = 'Sold out';
			tagColour = 'red';
			disabled = !store.isStaff;
		} else if (!(capacityRestriction?.showCapacity ?? true) && !store.isStaff) {
			// If a capacity is set to `ShowCapacity` false, don't show it to customers
			tagText = 'Available';
			tagColour = 'green';
		} else if (capacityRestriction!.remainingCapacity < 0 && store.isStaff) {
			tagText = `Overbooked by ${Math.abs(capacityRestriction?.remainingCapacity ?? 0)}`;
			tagColour = 'red';
		} else if (remainingPercentage <= 20 || capacityRestriction!.remainingCapacity < 3) {
			tagText = `Only ${capacityRestriction?.remainingCapacity} left`;
			tagColour = 'orange';
		} else {
			tagText = `${capacityRestriction?.remainingCapacity} available`;
			tagColour = 'green';
		}
	}
	if (!availableForRouteAndOrFerryTripBookingWizardData) {
		tagText = 'Unavailable';
		tagColour = 'grey';
		disabled = true;
	}

	// Hide capacity info from customers for staff-only add-ons
	if (isRestrictedLocked) {
		tagText = null;
		tagColour = null;
	}

	// Show "No longer available" tag for disabled add-ons (during alteration with existing quantity)
	if (effectivelyDisabled) {
		tagText = 'No longer available';
		tagColour = 'orange';
		// Don't set disabled = true; canNotBeAdded handles the add cap based on original amounts
	}

	return (
		<div
			className={classNames(
				'add-on__input-counter',
				disabled ? 'disabled' : '',
				hasBeenChangedByTheSystem() ? 'changed' : '',
			)}
		>
			<div className="add-on__input-counter__header">
				{/* eslint-disable-next-line no-nested-ternary */}
				<p className="direction-name">
					{direction === 'DEPARTURE'
						? `Departing ${getStopByIdIfAny(wizardData.departingJourney?.startStopId)?.location.name ?? 'trip'}`
						: isNotNullOrUndefined(
									getStopByIdIfAny(wizardData.returningJourney?.startStopId)?.location.name,
							  )
							? `Departing ${getStopByIdIfAny(wizardData.returningJourney?.startStopId)?.location.name}`
							: 'Returning trip'}
				</p>
				<If condition={isNotNullOrUndefined(tagText)}>
					<div className={`custom-tag ${tagColour}`}>
						<p>{tagText}</p>
					</div>
				</If>
			</div>
			<p className="date">{date()}</p>
			<div
				className={classNames(
					'add-on__input-counter__footer',
					hasBeenChangedByTheSystem() ||
						addOn.isSingular ||
						effectivelyDisabled ||
						isRestrictedLocked ||
						(addOn.isRestricted && canAdjustRestrictedAddOns())
						? ''
						: 'flex-end',
				)}
			>
				<div className="add-on__input-counter__info-list">
					<If condition={addOn.isSingular}>
						<div className="add-on__input-counter__tool-tip info">
							<CustomTooltip content="Maximum of 1 of these add-ons per booking in each direction." />
							<p>Maximum of one</p>
						</div>
					</If>
					{hasBeenChangedByTheSystem() && (
						<div className="add-on__input-counter__tool-tip warning">
							<Icon name="warning" />
							<p>Changed availability</p>
						</div>
					)}
					<If condition={isRestrictedLocked}>
						<div className="add-on__input-counter__tool-tip info">
							<span className="icon-warning icon-only" style={{ color: '#e8860c', marginRight: '4px' }} />
							<p>Contact staff to make changes</p>
						</div>
					</If>
					<If condition={addOn.isRestricted && canAdjustRestrictedAddOns()}>
						<div className="add-on__input-counter__tool-tip info">
							<span className="icon-warning icon-only" style={{ color: '#e8860c', marginRight: '4px' }} />
							<p>Staff only</p>
						</div>
					</If>
				</div>
				<AddOnInputCounter
					amount={amount()}
					onAdd={onAdd}
					onSubtract={onSubtract}
					addDisabled={addDisabled() || isRestrictedLocked}
					subtractDisabled={subtractDisabled() || isRestrictedLocked}
					addBtnAlt={`add-${addOn.name}`}
					subtractBtnAlt={`subtract-${addOn.name}`}
				/>
			</div>
		</div>
	);
}
